import type { AuditFindingPartial, AuditRule } from "../types";
import { addressFromRC } from "../addressing";
import { Origin, ValueKind } from "../../../model";

/**
 * If a column is "mostly formulas" (≥60% of non-empty cells), flag the
 * hardcoded numeric cells in it — they're likely overrides that should be
 * formulas or that escape audit because they look like results.
 */

const MIN_NON_EMPTY = 3;
const FORMULA_MAJORITY = 0.6;

export const hardcodedInFormulaColumn: AuditRule = {
  id: "system.audit.hardcoded-in-formula-column",
  name: "Hardcoded value in a formula column",
  description:
    "In a column where most cells are formulas, a cell containing a hardcoded number is often an unintended override.",
  enabled: true,
  source: "system",
  severity: "warning",
  /**
   * The two-axis classification makes this rule almost a direct
   * readout: count cells where origin === FORMULA vs origin ===
   * CONSTANT and a numeric value-kind. No format inspection or
   * value-type translation needed — the sink already encoded the
   * distinction.
   *
   * Latent improvement: once the spill catalog is populated (Phase
   * 5a), spilled cells will read origin === SPILL_RESULT and be
   * excluded automatically.
   */
  check(sheet, model) {
    void model;
    const findings: AuditFindingPartial[] = [];
    const { rows, cols, startRow, startCol } = sheet.bounds;
    const cellOrigin = sheet.cellOrigin;
    const cellValueKind = sheet.cellValueKind;
    const numericGrid = sheet.numericGrid;

    for (let c = 0; c < cols; c++) {
      let formulaCount = 0;
      const hardcoded: Array<{ row: number; value: number }> = [];

      for (let r = 0; r < rows; r++) {
        const idx = r * cols + c;
        const origin = cellOrigin[idx];
        if (origin === Origin.FORMULA) {
          formulaCount++;
        } else if (origin === Origin.CONSTANT) {
          const vk = cellValueKind[idx];
          if (vk === ValueKind.NUMBER || vk === ValueKind.DATE) {
            hardcoded.push({ row: r, value: numericGrid[idx] });
          }
        }
      }

      const nonEmpty = formulaCount + hardcoded.length;
      if (nonEmpty < MIN_NON_EMPTY) continue;
      if (formulaCount / nonEmpty < FORMULA_MAJORITY) continue;

      for (const { row, value } of hardcoded) {
        findings.push({
          sheetName: sheet.name,
          address: addressFromRC(startRow + row, startCol + c),
          message: `hardcoded value ${String(value)} in a column that's mostly formulas`,
        });
      }
    }

    return findings;
  },
};
